import { assign, createMachine } from 'xstate';
import { LDClient, LDFlags } from '~/providers';
import { fetchVnmTerms } from '~/services';
import { ContractTag } from '~/typings/graphql';
import { PROMO_CODES } from '~/utils/constants';
import { fetchContractLanguage } from '~/utils/fetch-contract-language';
import { contextCreator } from '../utils/context';
import {
  UploadContractsContext,
  UploadContractsEvent,
  UploadContractsServices,
  UploadContractsDoneData,
} from './types';

export const uploadContractsMachine = () =>
  createMachine(
    {
      tsTypes: {} as import('./machine.typegen').Typegen0,
      predictableActionArguments: true,
      schema: {
        context: {} as UploadContractsContext,
        events: {} as UploadContractsEvent,
        services: {} as UploadContractsServices,
      },
      context: {
        funnelPacket: {} as UploadContractsContext['funnelPacket'],
        docuSignContracts: [] as UploadContractsContext['docuSignContracts'],
        vnmSolarProjectContracts: [] as UploadContractsContext['vnmSolarProjectContracts'],
      },
      id: 'uploadContractsMachine',
      initial: 'start',
      states: {
        start: {
          always: { target: 'uploadContracts' },
        },
        uploadContracts: {
          initial: 'fetching',
          states: {
            fetching: {
              invoke: {
                id: 'fetchTerms',
                src: 'fetchTerms',
                onDone: { target: 'idle', actions: 'saveFetchTermsData' },
              },
            },
            idle: {
              on: {
                SUBMIT_CONTRACTS: [{ target: '#completed' }],
              },
            },
          },
        },
        completed: {
          id: 'completed',
          type: 'final',
          data: (context): UploadContractsDoneData => ({
            funnelPacket: context.funnelPacket,
            docuSignContracts: context.docuSignContracts,
            vnmSolarProjectContracts: context.vnmSolarProjectContracts,
          }),
        },
      },
    },
    {
      actions: {
        saveFetchTermsData: assign((_context, event) => {
          return {
            docuSignContracts: event.data.docuSignContracts,
            showLmi: event.data.showLmi,
            vnmSolarProjectContracts: event.data.vnmSolarProjectContracts,
          };
        }),
      },
      services: {
        fetchTerms: async context => {
          const contractTags = {
            [PROMO_CODES.ENERGY_WELL_SIGNUP]: 'energywell',
            [PROMO_CODES.POWER_SETTER_DATA_ENTRY]: 'powersetter',
          };
          const tag = context.funnelPacket.user.signupPromoCode
            ? contractTags[context.funnelPacket.user.signupPromoCode?.code!]
            : undefined;
          const {
            docuSignContracts,
            showLmi: showLmiFromTerms,
            generalContracts,
            vnmSolarProjectContracts,
          } = await fetchVnmTerms({
            stateId: context.funnelPacket.utility.state.id,
            utilityAccountId: context.funnelPacket.utilityAccount.id,
            contractTags: tag ? [tag as ContractTag] : undefined,
          });

          const lmiSelfAttestationStates = LDClient.variation(LDFlags.lmiSelfAttestationState);
          const showLmi =
            showLmiFromTerms &&
            lmiSelfAttestationStates.includes(context.funnelPacket?.utility.state.abbreviation);

          const allSolarContracts = generalContracts
            ? vnmSolarProjectContracts?.concat(generalContracts)
            : vnmSolarProjectContracts;

          const data = fetchContractLanguage(docuSignContracts, allSolarContracts, showLmi);

          return data;
        },
      },
    },
  );

export const uploadContractsContext = contextCreator<UploadContractsContext, 'funnelPacket'>(
  uploadContractsMachine,
);
